﻿import React, { useEffect } from 'react';

import { getStartEndDateDefaultDays, SINCE_START_DAYS } from 'pages/reports/utils/reportDateDefaults';
import { GMetrixDateRangePicker } from 'components/GMetrixDateRangePicker';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedMessage, useIntl } from 'react-intl';
import { Report, ReportFilter } from 'apis/ReportApi';
import { getFormattedDate } from 'utils';
import { useSuperState } from 'hooks';

interface StartDateFilterProps {
    dateValidation?: {
        allowFutureDates?: boolean;
        allowPastDates?: boolean;
        maxDate?: Date | null;
        minDate?: Date | null;
    };
    report: Report;
    reportFilterIndex: number;
    reportFilters: ReportFilter[];
    resetVersion?: number;
    setReport: (report: Report) => void;
    urlSync?: boolean;
}

const StartDateFilter = ({
    dateValidation,
    report,
    reportFilterIndex,
    reportFilters,
    resetVersion = 0,
    setReport,
    urlSync = true
}: StartDateFilterProps) => {

    const intl = useIntl();

    const presets = [
        { id: 1, days: 7000, label: intl.formatMessage({ id: 'since_start.7a6d9683', defaultMessage: 'Since Start' }) },
        { id: 2, days: 90, label: intl.formatMessage({ id: '90_days.7a6c1d5a', defaultMessage: '90 Days' }) },
        { id: 3, days: 60, label: intl.formatMessage({ id: '60_days.41723172', defaultMessage: '60 Days' }) },
        { id: 4, days: 30, label: intl.formatMessage({ id: '30_days.4fb82e85', defaultMessage: '30 Days' }) },
        { id: 5, days: 7, label: intl.formatMessage({ id: '7_days.26df6f04', defaultMessage: '7 Days' }) },
        { id: 6, days: null, label: intl.formatMessage({ id: 'custom.2a5a9a35', defaultMessage: 'Custom' }) }
    ];

    // Reports that should default to a 7-day date window instead of "Since Start".
    const defaultPresetDays = getStartEndDateDefaultDays(report.Id);
    const defaultPreset = presets.find(p => p.days === defaultPresetDays) ?? presets[0];

    const getInitialDates = (): { end: string; start: string } => {

        const filterValue = reportFilters[reportFilterIndex]?.Value;
        if (filterValue && filterValue.includes('|')) {

            const [s, e] = filterValue.split('|');
            return { end: e, start: s };

        }
        return { end: today(), start: subtractDays(defaultPresetDays) };

    };
    const initialDates = getInitialDates();

    const [state, setState] = useSuperState({
        endDate: initialDates.end,
        selectedPreset: defaultPreset,
        startDate: initialDates.start
    });
    const { endDate, selectedPreset, startDate } = state;

    /* ------------------ helpers ------------------ */

    const today = () => getFormattedDate(new Date());

    const subtractDays = (days: number) => {

        const d = new Date();
        d.setDate(d.getDate() - days);
        return getFormattedDate(d);

    };

    const isSameDate = (d1: Date, d2: Date) => d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();

    const normalizeDate = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const getEffectiveDateBounds = () => {

        const todayDate = normalizeDate(new Date());
        const hasMinDate = !!dateValidation?.minDate;
        const hasMaxDate = !!dateValidation?.maxDate;

        let minDate = hasMinDate ? normalizeDate(dateValidation!.minDate as Date) : null;
        let maxDate = hasMaxDate ? normalizeDate(dateValidation!.maxDate as Date) : null;

        if (dateValidation?.allowPastDates === false) {

            minDate = minDate ? (minDate > todayDate ? minDate : todayDate) : todayDate;

        }

        if (dateValidation?.allowFutureDates === false) {

            maxDate = maxDate ? (maxDate < todayDate ? maxDate : todayDate) : todayDate;

        }

        return { maxDate, minDate };

    };

    const clampDateToBounds = (date: Date | null) => {

        if (!date) return null;

        const normalizedDate = normalizeDate(date);
        const { maxDate, minDate } = getEffectiveDateBounds();

        if (minDate && normalizedDate < minDate) {

            return minDate;

        }

        if (maxDate && normalizedDate > maxDate) {

            return maxDate;

        }

        return normalizedDate;

    };

    const sanitizeDateRange = (start: Date | null, end: Date | null) => {

        const sanitizedStart = clampDateToBounds(start);
        let sanitizedEnd = clampDateToBounds(end);

        if (sanitizedStart && sanitizedEnd && sanitizedStart > sanitizedEnd) {

            sanitizedEnd = sanitizedStart;

        }

        return { sanitizedEnd, sanitizedStart };

    };

    const getPresetForDates = (start: string, end: string) => {

        const parsedStart = parseDateString(start);
        const parsedEnd = parseDateString(end);

        if (!parsedStart || !parsedEnd) {

            return presets[0];

        }

        const todayDate = new Date();
        const matchedPreset = presets.find(preset => {

            if (!preset.days) return false;

            const expectedStart = new Date();
            expectedStart.setDate(todayDate.getDate() - preset.days);

            return isSameDate(parsedStart, expectedStart) && isSameDate(parsedEnd, todayDate);

        });

        const daysFromToday = Math.floor((todayDate.getTime() - parsedStart.getTime()) / (1000 * 60 * 60 * 24));
        if (daysFromToday >= 3650 && isSameDate(parsedEnd, todayDate)) {

            return presets[0];

        }

        return matchedPreset ?? presets[5];

    };

    const parseDateString = (value: string): Date | null => {

        if (!value) return null;

        const slashFormatMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (slashFormatMatch) {

            const month = parseInt(slashFormatMatch[1], 10) - 1;
            const day = parseInt(slashFormatMatch[2], 10);
            const year = parseInt(slashFormatMatch[3], 10);
            const parsedDate = new Date(year, month, day);

            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;

        }

        const parsedDate = new Date(value.includes('T') ? value : `${value}T00:00:00`);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;

    };

    const formatDateForLabel = (value: string) => {

        const parsedDate = parseDateString(value);
        if (!parsedDate) return value;
        return intl.formatDate(parsedDate, { day: 'numeric', month: 'short', year: 'numeric' });

    };

    const syncFromRangeValue = (rangeValue?: string) => {

        let start: string;
        let end: string;

        if (rangeValue && rangeValue.includes('|')) {

            [start, end] = rangeValue.split('|');

        } else {

            const fallbackPreset = defaultPreset;
            start = subtractDays(fallbackPreset.days || 7000);
            end = today();

        }

        const parsedStart = parseDateString(start);
        const parsedEnd = parseDateString(end);
        const { sanitizedEnd, sanitizedStart } = sanitizeDateRange(parsedStart, parsedEnd);

        const sanitizedStartText = sanitizedStart ? getFormattedDate(sanitizedStart) : '';
        const sanitizedEndText = sanitizedEnd ? getFormattedDate(sanitizedEnd) : '';

        setState({
            endDate: sanitizedEndText,
            selectedPreset: getPresetForDates(sanitizedStartText, sanitizedEndText),
            startDate: sanitizedStartText
        });

        return { sanitizedEndText, sanitizedStartText };

    };

    /* ------------------ init from report + URL ------------------ */

    useEffect(() => {

        // Check URL params first (for refresh persistence). Embedded usages
        // (urlSync=false) share the host page URL across several stacked reports,
        // so they must neither read nor write the StartEndDate param — otherwise
        // one report's (possibly stale, pre-midnight) range leaks into the others.
        const url = new URL(window.location.href);
        const urlDateParam = urlSync ? url.searchParams.get('StartEndDate') : null;

        const filterValue = reportFilters[reportFilterIndex]?.Value;
        const initialRange = urlDateParam && urlDateParam.includes('|') ? urlDateParam : filterValue;
        const { sanitizedEndText, sanitizedStartText } = syncFromRangeValue(initialRange);

        if (urlSync) {

            url.searchParams.set('StartEndDate', `${sanitizedStartText}|${sanitizedEndText}`);
            window.history.pushState(null, '', url);

        }

    }, []);

    useEffect(() => {

        const filterValue = reportFilters[reportFilterIndex]?.Value;
        if (!filterValue || !filterValue.includes('|')) {

            return;

        }

        if (filterValue === `${startDate}|${endDate}`) {

            return;

        }

        syncFromRangeValue(filterValue);

    }, [reportFilters, reportFilterIndex]);

    useEffect(() => {

        if (resetVersion <= 0) {

            return;

        }

        const sinceStartPreset = defaultPreset;
        const sinceStartStart = subtractDays(sinceStartPreset.days || 7000);
        const sinceStartEnd = today();

        setState({
            endDate: sinceStartEnd,
            selectedPreset: sinceStartPreset,
            startDate: sinceStartStart
        });

    }, [resetVersion]);

    useEffect(() => {

        applyDates();

    }, [startDate, endDate]);

    /* ------------------ preset auto-detection ------------------ */

    useEffect(() => {

        if (selectedPreset.label === 'Custom') {

            return;

        }

        const todayDate = new Date();

        const matched = presets.find(p => {

            if (!p.days) return false;

            const expectedStart = new Date();
            expectedStart.setDate(todayDate.getDate() - p.days);

            return (
                isSameDate(new Date(startDate), expectedStart) &&
                isSameDate(new Date(endDate), todayDate)
            );

        });

        setState({ selectedPreset: matched ?? presets[0] }); // Custom fallback

    }, [startDate, endDate]);

    /* ------------------ preset click behavior ------------------ */

    const onPresetClick = (preset: typeof presets[number]) => {

        if (preset.days) {

            const presetStartDate = new Date();
            presetStartDate.setDate(presetStartDate.getDate() - preset.days);
            const presetEndDate = new Date();

            const { sanitizedEnd, sanitizedStart } = sanitizeDateRange(presetStartDate, presetEndDate);

            setState({
                endDate: sanitizedEnd ? getFormattedDate(sanitizedEnd) : '',
                selectedPreset: preset,
                startDate: sanitizedStart ? getFormattedDate(sanitizedStart) : ''
            });

        } else {

            setState({ selectedPreset: preset });

        }

    };

    /* ------------------ apply changes ------------------ */

    const applyDates = () => {

        const updatedFilters = [...reportFilters];
        updatedFilters[reportFilterIndex].Value = `${startDate}|${endDate}`;

        setReport({ ...report, ReportFilters: updatedFilters });

        if (urlSync) {

            const url = new URL(window.location.href);
            url.searchParams.set('StartEndDate', `${startDate}|${endDate}`);
            window.history.pushState(null, '', url);

        }

    };

    /* ------------------ render ------------------ */

    return (
        <div className="mt-3">
            <label className="fw-bold">
                <FormattedMessage
                    values={{
                        end: formatDateForLabel(endDate),
                        start: selectedPreset.id === 1 ? selectedPreset.label : formatDateForLabel(startDate)
                    }} defaultMessage="Date Window ({start} - {end})"
                    id="date_window_start_end.4a4fa11f"
                />
            </label>

            <div className="btn-group d-flex flex-wrap mt-2">
                {presets.map(preset => (
                    <GMetrixButton
                        className=" mt-2"
                        key={preset.id}
                        onClick={() => onPresetClick(preset)}
                        style={{ width: '110px' }}
                        variant={selectedPreset.id === preset.id ? 'primary' : 'secondary'}
                    >
                        {preset.label}
                    </GMetrixButton>
                ))}
            </div>

            {selectedPreset.label === 'Custom' && (
                <div className="mt-3">
                    <GMetrixDateRangePicker
                        onChange={(start, end) => {

                            const { sanitizedEnd, sanitizedStart } = sanitizeDateRange(start, end);
                            setState({
                                endDate: sanitizedEnd ? getFormattedDate(sanitizedEnd) : '',
                                startDate: sanitizedStart ? getFormattedDate(sanitizedStart) : ''
                            });

                        }}
                        endDate={parseDateString(endDate)}
                        maxDate={getEffectiveDateBounds().maxDate}
                        minDate={getEffectiveDateBounds().minDate}
                        placeholder={intl.formatMessage({ id: 'select_date_range.41ecb8e8', defaultMessage: 'Select date range...' })}
                        startDate={parseDateString(startDate)}
                    />
                </div>
            )}
        </div>
    );

};

export default StartDateFilter;
